import { FolderOpen } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { ProjectStatusFilter } from "@/domains/projects/status/ProjectStatusFilter";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";
import type { Channel } from "./ChannelDropdown";

interface ChannelSwitcherProps {
  channels: Channel[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

/** Vertical project-channel list for the expanded modal's left column. */
export function ChannelSwitcher({ channels, selectedId, onSelect }: ChannelSwitcherProps) {
  const { t } = useLanguage();
  const statusFilter = useProjectStatusFilter(channels, selectedId);
  return (
    <div className="flex w-56 flex-shrink-0 flex-col border-r bg-muted/20">
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t.messages.channelsHeading}
      </div>
      {statusFilter.showFilter && (
        <ProjectStatusFilter
          variant="inline"
          value={statusFilter.filter}
          onValueChange={statusFilter.setFilter}
          counts={statusFilter.counts}
          className="mx-2 mb-2"
        />
      )}
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {statusFilter.visible.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onSelect(c.id)}
            className={cn(
              "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
              c.id === selectedId
                ? "bg-accent font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/60"
            )}
          >
            <FolderOpen className="h-4 w-4 flex-shrink-0 text-primary" />
            <ProjectStatusBadge variant="dot" status={c.status} />
            <span className="truncate">{c.name}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
